/**
 * Monfiora — localStorage-based wishlist module.
 *
 * Stores variant IDs (numeric strings) in localStorage under
 * `monfiora.wishlist` as a JSON array. Dispatches a `wishlist:change`
 * CustomEvent on document whenever the set changes.
 *
 * No external deps. Survives page navigations, browser restarts.
 * Cleared if the user clears site data.
 */
(function () {
  const KEY = 'monfiora.wishlist';

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(String);
    } catch (e) {
      return [];
    }
  }

  function write(arr) {
    try {
      localStorage.setItem(KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn('[monfiora-wishlist] localStorage write failed', e);
    }
  }

  function emit(detail) {
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail }));
  }

  const Wishlist = {
    has(variantId) {
      if (variantId == null) return false;
      return read().includes(String(variantId));
    },
    getAll() {
      return read();
    },
    count() {
      return read().length;
    },
    add(variantId) {
      if (variantId == null) return false;
      const id = String(variantId);
      const list = read();
      if (list.includes(id)) return false;
      list.push(id);
      write(list);
      emit({ action: 'add', variantId: id, count: list.length });
      return true;
    },
    remove(variantId) {
      if (variantId == null) return false;
      const id = String(variantId);
      const list = read();
      const idx = list.indexOf(id);
      if (idx === -1) return false;
      list.splice(idx, 1);
      write(list);
      emit({ action: 'remove', variantId: id, count: list.length });
      return true;
    },
    toggle(variantId) {
      if (this.has(variantId)) {
        this.remove(variantId);
        return false;
      } else {
        this.add(variantId);
        return true;
      }
    },
    clear() {
      write([]);
      emit({ action: 'clear', count: 0 });
    },
  };

  window.MonfioraWishlist = Wishlist;

  // ── Heart button delegation ─────────────────────────────────────
  // Any element with data-monfiora-heart and data-variant-id will
  // toggle membership when clicked. Visual state is a CSS class.
  function syncOne(el) {
    const id = el.getAttribute('data-variant-id');
    const inList = Wishlist.has(id);
    el.classList.toggle('is-active', inList);
    el.setAttribute('aria-pressed', String(inList));
    el.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAll() {
    document.querySelectorAll('[data-monfiora-heart]').forEach(syncOne);
    // Update count badges
    const c = Wishlist.count();
    document.querySelectorAll('[data-monfiora-wishlist-count]').forEach(el => {
      el.textContent = c;
      el.classList.toggle('is-empty', c === 0);
    });
  }

  document.addEventListener('click', (e) => {
    const heart = e.target.closest('[data-monfiora-heart]');
    if (!heart) return;
    e.preventDefault();
    e.stopPropagation();
    const id = heart.getAttribute('data-variant-id');
    if (!id) return;
    Wishlist.toggle(id);
  });

  document.addEventListener('wishlist:change', syncAll);

  // Initial sync — handle DOMContentLoaded vs already-ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }

  // Cross-tab sync — if the user has the wishlist open in two tabs
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) syncAll();
  });
})();
